// FILE: src/components/dashboard/EditProfileModal.jsx

import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';

export default function EditProfileModal({ isOpen, onClose, user, onUpdate }) {
  console.log('[EditProfileModal] 🎨 Rendering:', { isOpen, user });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      console.log('[EditProfileModal] 📝 Setting form data from user:', user);
      setFormData({
        name: user.fullName || user.name || '', // FIX: Check fullName first
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[EditProfileModal] 💾 Submitting:', formData);
    setLoading(true);
    try {
      const response = await apiClient.patch('/users/profile', formData);
      console.log('[EditProfileModal] ✅ Success:', response.data);
      toast.success('Profile updated successfully');
      onUpdate(response.data.user);
      onClose();
    } catch (error) {
      console.error('[EditProfileModal] ❌ Error:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header - responsive padding and text */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b">
          <h2 className="text-base sm:text-lg md:text-xl font-bold">Edit Profile</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md transition">
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Form - responsive padding and spacing */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          {/* Name field */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 text-xs sm:text-sm border rounded-md focus:ring-2 focus:ring-primary h-9 sm:h-10"
              required
            />
          </div>

          {/* Email field */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 text-xs sm:text-sm border rounded-md focus:ring-2 focus:ring-primary h-9 sm:h-10"
              required
            />
          </div>

          {/* Phone field */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="block text-xs sm:text-sm font-medium">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 text-xs sm:text-sm border rounded-md focus:ring-2 focus:ring-primary h-9 sm:h-10"
              placeholder="+254..."
            />
          </div>

          {/* Buttons - responsive gap and text */}
          <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm border rounded-md hover:bg-gray-50 transition h-9 sm:h-10"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition disabled:opacity-50 flex items-center justify-center gap-1.5 sm:gap-2 h-9 sm:h-10"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                  <span className="hidden sm:inline">Saving...</span>
                  <span className="sm:hidden">Save...</span>
                </>
              ) : (
                <>
                  <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Save Changes</span>
                  <span className="sm:hidden">Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}